import { useContext, useEffect, useState } from 'react';

import { useErrorBoundary } from 'react-error-boundary';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { ReferralContext } from '@contexts/referral-context';
import { observer } from 'mobx-react-lite';

import { sendAnalyticEvent } from '@app/web-analytic';

import { AnalyticEvent, CANDIDATE_AUTH } from '@shared/constants';
import {
  BoundaryErrorType,
  FlkCode,
  FullWidthPrimaryButton,
  addTestAttribute,
  getMismatches,
  phoneValueCasting,
  useStores,
} from '@shared/index';

import { type NewCandidateRequestParams, useNewCandidate } from '../../api';
import { useCandidateRegistrationSearchParams } from '../../hooks';
import {
  CandidateRegistrationStep,
  CandidateRegistrationTab,
  type RegistrationFormParams,
} from '../../model';

export const SubmitRegistrationForm = observer(() => {
  const { t } = useTranslation('CANDIDATE_REGISTRATION');
  const navigate = useNavigate();
  const { createReferralSearchParams } = useCandidateRegistrationSearchParams();
  const { showBoundary } = useErrorBoundary();
  const {
    MainStore: {
      agentUserStore: { setNewCandidate: setNewCandidateToStore },
    },
  } = useStores();

  const { referralData, setIsIncorrectLinkError } = useContext(ReferralContext);
  const { refMagCode, refDeptCode, refDeptId, refBranchId, linkId } =
    referralData;

  const [requestParams, setRequestParams] = useState<NewCandidateRequestParams>(
    {
      newCandidate: {
        nickname: '',
        phone: '',
      },
    }
  );

  const {
    handleSubmit,
    formState: { errors },
    setError,
  } = useFormContext<RegistrationFormParams>();

  const { res, error, isFetching } = useNewCandidate(
    {
      ...requestParams,
    },
    [requestParams]
  );

  useEffect(() => {
    if (!isFetching && res) {
      setNewCandidateToStore(requestParams.newCandidate);

      const { search } = createReferralSearchParams({
        step: CandidateRegistrationStep.CREATE_PASSWORD,
        tab: CandidateRegistrationTab.REGISTRATION,
        refDeptCode,
        refMagCode,
        refDeptId,
        refBranchId,
        linkId,
      });

      navigate({ pathname: CANDIDATE_AUTH, search });
    }
  }, [
    isFetching,
    res,
    requestParams,
    refDeptCode,
    refMagCode,
    refDeptId,
    refBranchId,
    linkId,
  ]);

  const submitPage = handleSubmit(({ nickname, phone: formattedPhone }) => {
    sendAnalyticEvent(AnalyticEvent.CANDIDATE_REG_TOSTEP_PASSWORD);

    const phone = phoneValueCasting(formattedPhone);

    setRequestParams({
      newCandidate: { nickname, phone },
      ...(refDeptCode && { refDeptCode }),
      ...(refMagCode && { refMagCode }),
      ...(refDeptId && { refDeptId }),
      ...(refBranchId && { refBranchId }),
    });
  });

  useEffect(() => {
    if (error) {
      const mismatches = getMismatches(error);
      if (!!mismatches) {
        let hasKnownErrors = false;
        mismatches.forEach(({ code }) => {
          if ([FlkCode.AGT_0901_0002, FlkCode.AGT_0901_0003].includes(code)) {
            // Пользователь с таким телефоном существует
            setError('phone', {
              type: 'value',
            });
            hasKnownErrors = true;
          } else if ([FlkCode.AGT_0901_0005].includes(code)) {
            setIsIncorrectLinkError(true);
            hasKnownErrors = true;
          }
        });

        if (hasKnownErrors) {
          return;
        }
      }

      showBoundary(BoundaryErrorType.SomethingWentWrong);
    }
  }, [error]);

  return (
    <FullWidthPrimaryButton
      size='l'
      onClick={submitPage}
      disabled={!!Object.keys(errors).length}
      isLoading={isFetching}
      {...addTestAttribute('candidate-registration-form-submit')}
    >
      {t('COMMON:buttons.continue')}
    </FullWidthPrimaryButton>
  );
});
